import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { PartConfigurationBomApi } from "src/api/generated/erp/configurator/api/partConfigurationBomApi.ts";
import i18n from "i18next";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { faTable, faTools } from "@fortawesome/pro-regular-svg-icons";
import { PartConfigurationDetailsView } from "src/components/views/erp/configurator/inspecting/PartConfigurationDetailsView.tsx";
import { PartConfigurationBomLineForm } from "src/components/views/erp/configurator/inspecting/PartConfigurationBomLineForm.tsx";
import { getPartConfigurationBomDataGridColumns } from "src/components/views/erp/configurator/inspecting/configurationBomUtils.ts";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { SitePartRevisionProductStructureView } from "src/components/views/erp/parts/siteParts/bom/SitePartRevisionProductStructureView.tsx";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { PartConfigurationBomLineConfiguratorView } from "src/components/views/erp/configurator/configuratorForm/impl/PartConfigurationBomLineConfiguratorView.tsx";
import { AsyncMenuButton } from "../../../../common/contextMenu/AsyncMenuButton.tsx";
import { PartConfigurationDetailsViewApi_PartConfigurationDto } from "src/api/generated/erp/configurator/api/partConfigurationDetailsViewApi.ts";
import { PartConfigurationBomLineInspectionDto } from "src/api/generated/erp/configurator/api/partConfigurationBomLineInspectionDto.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { ConfigurationManualEditContext } from "src/api/generated/erp/configurator/manualEdit/configurationManualEditContext.ts";

export interface PartConfigurationBomDataGridProps {
	partConfiguration: PartConfigurationDetailsViewApi_PartConfigurationDto;
	partConfiguratorType: PartConfiguratorType;
	editable: boolean;
	vatPercent?: number;
	manualEditContext: ConfigurationManualEditContext;
}

export const PartConfigurationBomDataGrid = ({
	partConfiguration,
	partConfiguratorType,
	editable,
	vatPercent,
	manualEditContext,
}: PartConfigurationBomDataGridProps) => {
	const { openDialog } = useGenericDialog();
	const showCostInSellingCurrency = partConfiguration.sellingCurrencyId !== partConfiguration.domesticCurrencyId;
	const useDomesticCostForMargin = partConfiguration.sellingCurrencyId === partConfiguration.domesticCurrencyId;

	return (
		<ClientSideDataGridModel
			gridId="CBAF44A180B56A68"
			fetchData={({ searchQuery }) =>
				PartConfigurationBomApi.getPartConfigurationBomLines({
					partConfigurationId: partConfiguration.partConfigurationId,
					searchQuery,
				})
			}
			initialParams={{ searchQuery: "" }}
			getRows={(data) => data}
			getRowId={(row) => row.partConfigurationBomLineId}
			render={({ dataGridProps, onlySelectedRow, refreshData }) => {
				return (
					<FormCrudDataGrid<PartConfigurationBomLineInspectionDto>
						columns={getPartConfigurationBomDataGridColumns({
							vatPercent,
							sellingCurrencyCode: partConfiguration.sellingCurrencyCode,
							domesticCurrencyCode: partConfiguration.domesticCurrencyCode,
							showCostInSellingCurrency,
							useDomesticCostForMargin,
						})}
						disableMultipleRowSelection
						form={{
							component: ({ row, onCompleted, onFormEdited }) => (
								<PartConfigurationBomLineForm
									partConfiguration={partConfiguration}
									existingBomLine={row}
									onCompleted={onCompleted}
									onFormEdited={onFormEdited}
									refreshSourceView={() => refreshData()}
									manualEditContext={manualEditContext}
								/>
							),
							editEnabled: editable,
							addRowEnabled: editable,
							dialogSize: "md",
							dialogTitle: i18n.t("bom_line"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey("erp.configurator.partConfigurationBomLine.formDialog"),
								title: i18n.t("bom_line"),
								pathSegments:
									row == null ? undefined : [`${i18n.t("reference_no")} ${row.referenceNo}`],
								data: {
									partConfigurationId: partConfiguration.partConfigurationId,
									partConfigurationBomLineId: row?.partConfigurationBomLineId ?? null,
								},
							}),
						}}
						remove={
							editable ?
								{
									type: "enabled",
									action: ({ items }) =>
										PartConfigurationBomApi.deleteBomLines({
											partConfigurationBomLineIds: items.map(
												(item) => item.partConfigurationBomLineId,
											),
											manualEditContext,
										}),
								}
							:	{ type: "hidden" }
						}
						actionBarComponents={
							<>
								<AavoTextField
									label={i18n.t("search")}
									onSubmit={async (input) => {
										await refreshData({ searchQuery: input });
									}}
								/>
								{onlySelectedRow?.bomLinePartConfigurationId != null && (
									<AsyncButton
										label={i18n.t("sub_configuration")}
										icon={faTable}
										variant={"outlined"}
										onClick={async () => {
											openBomLineSubConfiguration(onlySelectedRow!);
										}}
									/>
								)}
								{onlySelectedRow &&
									onlySelectedRow.bomLinePartConfigurationId == null &&
									onlySelectedRow.bomLineActiveSitePartRevisionId != null && (
										<AsyncButton
											label={i18n.t("product_structure")}
											icon={faTable}
											variant={"outlined"}
											onClick={async () => {
												openBomLinePartRevisionProductStructure(onlySelectedRow!);
											}}
										/>
									)}
							</>
						}
						rowContextMenuComponents={({ row, onlySingleRowSelected }) => [
							editable && onlySingleRowSelected && row.linePartIsConfigurable && (
								<AsyncMenuButton
									key={"configure"}
									label={i18n.t("configure")}
									icon={faTools}
									onClick={async () => {
										openBomLineConfigurator(row.partConfigurationBomLineId);
									}}
								/>
							),
						]}
						onRowDoubleClick={({ row }) => {
							if (row.bomLinePartConfigurationId != null) openBomLineSubConfiguration(row);
							else if (row.bomLineActiveSitePartRevisionId != null)
								openBomLinePartRevisionProductStructure(row);
						}}
						agentContext={{
							key: defineAgentUiContextKey("erp.configurator.partConfigurationBom.grid"),
							data: {
								partConfigurationId: partConfiguration.partConfigurationId,
							},
							rowFields: [
								"partConfigurationBomLineId",
								"partConfigurationId",
								"bomLinePartId",
								"bomLinePartConfigurationId",
								"referenceNo",
							],
							selectedRowPathSegment: (row) => `${i18n.t("reference_no")} ${row.referenceNo}`,
						}}
						{...dataGridProps}
					/>
				);

				function openBomLineConfigurator(partConfigurationBomLineId: number) {
					openDialog(({ closeDialog, onDataDirtyStateChanged }) => ({
						title: i18n.t("configure"),
						size: "lg",
						content: (
							<PartConfigurationBomLineConfiguratorView
								partConfigurationBomLineId={partConfigurationBomLineId}
								manualEditContext={manualEditContext}
								closeDialog={closeDialog}
								refreshSourceView={() => refreshData()}
								onDataDirtyStateChanged={onDataDirtyStateChanged}
							/>
						),
						agentContext: {
							key: defineAgentUiContextKey(
								"erp.configurator.partConfigurationBomLineConfigurator.dialog",
							),
							title: i18n.t("configure"),
							data: {
								partConfigurationId: partConfiguration.partConfigurationId,
								partConfigurationBomLineId: partConfigurationBomLineId,
							},
						},
					}));
				}
			}}
		/>
	);

	function openBomLineSubConfiguration(row: PartConfigurationBomLineInspectionDto) {
		const bomLinePartConfigurationId = row.bomLinePartConfigurationId;
		if (bomLinePartConfigurationId == null) return;

		openDialog(() => ({
			title: i18n.t("configuration"),
			content: (
				<PartConfigurationDetailsView
					partConfigurationId={bomLinePartConfigurationId}
					partConfiguratorType={partConfiguratorType}
					manualEditContext={{
						...manualEditContext,
						partConfigurationBomLinePath: [
							...manualEditContext.partConfigurationBomLinePath,
							row.partConfigurationBomLineId,
						],
					}}
					vatPercent={vatPercent}
				/>
			),
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.bomLinePartConfiguration.dialog"),
				title: i18n.t("configuration"),
				pathSegments: [row.linePartNo],
				data: {
					partConfigurationId: bomLinePartConfigurationId,
					parentPartConfigurationId: partConfiguration.partConfigurationId,
					partConfigurationBomLineId: row.partConfigurationBomLineId,
				},
			},
		}));
	}

	function openBomLinePartRevisionProductStructure(row: PartConfigurationBomLineInspectionDto) {
		const revisionId = row.bomLineActiveSitePartRevisionId;
		if (revisionId == null) return;

		openDialog(() => ({
			title: i18n.t("product_structure"),
			content: <SitePartRevisionProductStructureView partRevisionId={revisionId} readOnly />,
			agentContext: {
				key: defineAgentUiContextKey("erp.configurator.partConfigurationBomLineProductStructure.dialog"),
				title: i18n.t("product_structure"),
				pathSegments: [row.linePartNo],
				data: {
					partConfigurationId: partConfiguration.partConfigurationId,
					partConfigurationBomLineId: row.partConfigurationBomLineId,
					partRevisionId: revisionId,
				},
			},
		}));
	}
};
